import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Pages } from "../../abstract/controller/enum/Pages";
import * as systemUtils from "../../abstract/controller/utils/systemUtils";
import { CFG_ALGORITHM, CFG_HEADER, CFG_SEPARATOR } from "../../abstract/names/GeneratorNames";
import {
  getData,
  getErrors,
  getGeneratorData,
  getProgress,
  getSortConfig,
  sortData,
} from "../../abstract/redux/slice/generatorSlice";
import { ButtonDefault } from "../../components/ui/buttons/ButtonDefault";
import { ButtonGenerate } from "../../components/ui/buttons/ButtonGenerate";
import DialogSelect from "../../components/ui/modal/DialogSelect";
import { ToastContext } from "../../components/ui/pop-ups/Toast";
import styles from "./GeneratedDataPage.module.scss";

const GeneratedDataPage = () => {
  const _formData = useSelector(getGeneratorData);
  const _rows = useSelector(getData);
  const _errors = useSelector(getErrors);
  const _progress = useSelector(getProgress);
  const { state } = useLocation();

  const _separator = _formData[CFG_SEPARATOR];
  const _header = _formData[CFG_HEADER];
  const _algorithm = _formData[CFG_ALGORITHM];

  const { name: _name } = state ?? { name: "" };

  const [_isGui, _setIsGui] = useState(true);

  const handleSwitchToGui = () => {
    _setIsGui(true);
  };

  const handleSwitchToRaw = () => {
    _setIsGui(false);
  };

  return (
    <>
      {_errors.length > 0 && <WindowModalError message={_errors.join("\n")} />}
      <Gen>
        <GenHeader>
          <GenHeaderNavigation />
          <GenHeaderTitle name={_name} />
        </GenHeader>
        <GenBody>
          <BodyInfo progress={_progress} algorithm={_algorithm} />
          <BodyHeader
            isGui={_isGui}
            handleSwitchToGui={handleSwitchToGui}
            handleSwitchToRaw={handleSwitchToRaw}
          />
          {_isGui ? (
            <GenBodyTable header={_header} rows={_rows.map((_row) => _row.split(_separator))} />
          ) : (
            <GenBodyRaw data={_rows.join("\n")} />
          )}
        </GenBody>
        <GenFooter>
          <GenFooterButtons data={_rows.join("\n")} />
        </GenFooter>
      </Gen>
    </>
  );
};

const BodyInfo = ({ progress, algorithm }) => {
  return (
    <div className={styles["info"]}>
      {progress && progress.total > 0 && <ProgressBar progress={progress} algorithm={algorithm} />}
    </div>
  );
};

const BodyHeader = ({ isGui, handleSwitchToGui, handleSwitchToRaw }) => {
  return (
    <div className={styles["header-display"]}>
      <div
        className={`${styles["header-display__button"]} ${
          isGui ? styles["header-display__button--selected"] : ""
        } `}
        onClick={handleSwitchToGui}>
        Table
      </div>
      <div
        className={`${styles["header-display__button"]} ${
          !isGui ? styles["header-display__button--selected"] : ""
        }`}
        onClick={handleSwitchToRaw}>
        Raw
      </div>
    </div>
  );
};

const Gen = ({ children }) => {
  return <div className={styles["page"]}>{children}</div>;
};

const GenHeader = ({ children }) => {
  return <div className={styles["header"]}>{children}</div>;
};

const GenHeaderNavigation = () => {
  const _navigate = useNavigate();

  const _handleBackToModel = () => {
    _navigate(Pages.ROOT);
  };

  return (
    <div className={styles["header__options"]}>
      <div className={styles["header__option"]}>
        <ButtonDefault text={"Back to model editor"} handler={_handleBackToModel} wide />
      </div>
    </div>
  );
};

const GenHeaderTitle = ({ name }) => {
  return (
    <div className={styles["header__name"]}>
      Generated tests for <span className={styles["header__name-title"]}>{name}</span>
    </div>
  );
};

const GenBody = ({ children }) => {
  return <div className={styles["body"]}>{children}</div>;
};

const GenBodyTable = ({ header, rows }) => {
  const dispatch = useDispatch();
  const sortConfig = useSelector(getSortConfig);

  if (!rows) {
    return null;
  }

  let _header = null;
  let _dataRows = rows;

  if (header) {
    _header = rows[0];
    _dataRows = rows.slice(1);
  }

  const handleSort = (columnIndex) => {
    const currentDirection =
      sortConfig.column === columnIndex && sortConfig.direction === "asc" ? "desc" : "asc";
    dispatch(sortData(columnIndex, currentDirection));
  };

  const getSortIndicator = (columnIndex) => {
    if (sortConfig.column !== columnIndex) {
      return (
        <span className={`${styles["sort-indicator"]} ${styles["sort-indicator--neutral"]}`}>
          {" "}
          ⇅{" "}
        </span>
      );
    }
    return sortConfig.direction === "asc" ? (
      <span className={styles["sort-indicator"]}> ⬆ </span>
    ) : (
      <span className={styles["sort-indicator"]}> ⬇ </span>
    );
  };

  return (
    <div className={styles["body__table"]}>
      <table>
        {_header && (
          <thead>
            <tr>
              {_header.map((cell, cellIndex) => (
                <th
                  key={cellIndex}
                  onClick={() => handleSort(cellIndex)}
                  style={{ cursor: "pointer" }}>
                  {cell}
                  {getSortIndicator(cellIndex)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {_dataRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GenBodyRaw = ({ data }) => {
  return (
    <div className={styles["body__raw"]}>
      <div className={styles["body__raw-data"]}>{data}</div>
    </div>
  );
};

const ProgressBar = ({ progress, algorithm, className = "" }) => {
  const { done, total, percentage } = progress;
  if (total === 0) return null;

  const isComplete = done === total;
  const isNwise = algorithm === "n-wise";
  return (
    <div className={`${styles["progress"]} ${className}`}>
      <div className={styles["progress__info"]}>
        <span className={styles["progress__text"]}>
          {isComplete
            ? isNwise
              ? `Total Tuples: ${total}`
              : `Total test cases: ${total}`
            : isNwise
              ? `Generating tuples: ${done} / ${total}`
              : `Generating test cases: ${done} / ${total}`}
        </span>
      </div>
      <div className={styles["progress__bar"]}>
        <div className={styles["progress__fill"]} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const GenFooter = ({ children }) => {
  return <div className={styles["footer"]}>{children}</div>;
};

const GenFooterButtons = ({ data }) => {
  const { addToast } = useContext(ToastContext);

  const _handleDownload = () => {
    systemUtils.downloadFile(data, "test.csv");
  };

  const handleCopy = () => {
    systemUtils.copyToClipboard(data);
    addToast("Data copied to clipboard!", "success");
  };

  return (
    <>
      <ButtonGenerate text={"Copy csv"} handler={handleCopy} secondary />
      <ButtonGenerate text={"Download csv"} handler={_handleDownload} />
    </>
  );
};

function WindowModalError({ message }) {
  const _navigate = useNavigate();

  const _handleBack = () => {
    _navigate(Pages.ROOT);
  };

  return (
    <DialogSelect
      textHeader="error"
      textBody={`${message}.`}
      actions={[
        {
          text: "go back",
          handler: () => {
            _handleBack(false);
          },
        },
      ]}
      cancel={() => {
        _handleBack(false);
      }}
    />
  );
}

export default GeneratedDataPage;
